/**
 * @module linkage/qbFields/qbLinkage/handlers/columnSort/syncFromQB/realignDimensionsOrder
 * @description column_sort 维区顺序对齐
 */

import type { IColumnSortItem } from '@/pages/datalab/pages/Multidimensional/Analysis/types/chartSettings/fieldColumnSort';
import { fieldKey, snapshotList } from '../../../fieldSnapshot';
import { getColumnSortItemId } from '../untils/keys';
import type { IColumnSortOpsContext } from './syncOps';
import {
  MAX_ALIGN_ITERATIONS,
  dedupeList,
  getColumnSortOrderSignature,
  getDimensionReorderKey,
  listQBSyncedDimensionItemsInOrder,
} from './realignColumnSortShared';

function desiredQBDimensionIdsInOrder(
  list: IColumnSortItem[],
  columnDimensions: unknown[],
): string[] {
  const present = new Set(
    listQBSyncedDimensionItemsInOrder(list, columnDimensions).map(item =>
      getDimensionReorderKey(item),
    ),
  );
  const ids: string[] = [];
  for (const snap of snapshotList(columnDimensions, 'dimension')) {
    const id = fieldKey(snap);
    if (present.has(id)) {
      ids.push(id);
    }
  }
  return ids;
}

function currentQBDimensionIdsInListOrder(
  list: IColumnSortItem[],
  columnDimensions: unknown[],
): string[] {
  return listQBSyncedDimensionItemsInOrder(list, columnDimensions).map(item =>
    getDimensionReorderKey(item),
  );
}

function qbDimensionOrderMatches(
  list: IColumnSortItem[],
  columnDimensions: unknown[],
): boolean {
  const desired = desiredQBDimensionIdsInOrder(list, columnDimensions);
  const current = currentQBDimensionIdsInListOrder(list, columnDimensions);
  if (desired.length !== current.length) {
    return false;
  }
  return desired.every((id, i) => id === current[i]);
}

function moveDimensionBefore(
  list: IColumnSortItem[],
  moved: IColumnSortItem,
  before: IColumnSortItem,
): IColumnSortItem[] {
  const moveId = getColumnSortItemId(moved);
  const beforeId = getColumnSortItemId(before);
  const rest = list.filter(item => getColumnSortItemId(item) !== moveId);
  const beforeIdx = rest.findIndex(
    item => getColumnSortItemId(item) === beforeId,
  );
  if (beforeIdx < 0) {
    return list;
  }
  const next = [...rest];
  next.splice(beforeIdx, 0, moved);
  return next;
}

export function alignDimensionsToQBOrder(
  list: IColumnSortItem[],
  ctx: IColumnSortOpsContext,
): IColumnSortItem[] {
  const columnDimensions = ctx.columnDimensions || [];
  if (!columnDimensions.length) {
    return list;
  }

  let next = dedupeList(list);
  let guard = 0;

  while (
    !qbDimensionOrderMatches(next, columnDimensions) &&
    guard < MAX_ALIGN_ITERATIONS
  ) {
    guard += 1;
    const desired = desiredQBDimensionIdsInOrder(next, columnDimensions);
    const syncedInList = listQBSyncedDimensionItemsInOrder(
      next,
      columnDimensions,
    );
    const current = syncedInList.map(item => getDimensionReorderKey(item));

    let targetIndex = -1;
    for (let i = 0; i < desired.length; i += 1) {
      if (desired[i] !== current[i]) {
        targetIndex = i;
        break;
      }
    }
    if (targetIndex < 0) {
      break;
    }

    const targetId = desired[targetIndex];
    const targetItem = syncedInList.find(
      item => getDimensionReorderKey(item) === targetId,
    );
    const wrongItem = syncedInList[targetIndex];
    if (!targetItem || !wrongItem) {
      break;
    }
    if (
      getDimensionReorderKey(targetItem) === getDimensionReorderKey(wrongItem)
    ) {
      break;
    }

    const updated = moveDimensionBefore(next, targetItem, wrongItem);
    if (
      getColumnSortOrderSignature(updated) === getColumnSortOrderSignature(next)
    ) {
      break;
    }
    next = updated;
  }

  return next;
}

/** @deprecated 使用 alignDimensionsToQBOrder */
export const alignMovableDimensionsToQBOrder = alignDimensionsToQBOrder;

export function realignDimensionsToQBOrder(
  list: IColumnSortItem[],
  ctx: IColumnSortOpsContext,
): IColumnSortItem[] {
  return alignDimensionsToQBOrder(dedupeList(list), ctx);
}
